/**
 * @fileoverview Rich-text editor for a generated report (MOR-556, Phase B)
 *
 * A focused TipTap editor with a formatting toolbar (font size, bold/italic,
 * headings, lists, alignment, tables, undo/redo) used when the practitioner
 * edits the assembled report body.
 */

import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { Mark, mergeAttributes } from "@tiptap/core";
import { Link, useNavigate } from "@tanstack/react-router";
import StarterKit from "@tiptap/starter-kit";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { TableKit } from "@tiptap/extension-table";
import { FontSizeNode } from "@/ui/composed/editor/font-size-node-extension";
import { ChartNode } from "@/components/agents/universal/canvas/ChartNode";
import { SpreadsheetNode } from "@/components/agents/universal/canvas/SpreadsheetNode";
import {
  canonicalSearchKind,
  getKindMeta,
} from "@/components/agents/universal/searchResultKinds";
import { SourceChips } from "@/components/agents/universal/sourceCitations";
import type { CitedSource } from "@/components/agents/universal/sourceCitations.helpers";
import { ReportCitationMarkers } from "./citationMarkersExtension";
import {
  buildCitationNumbers,
  citationsFromAttr,
  encodeCitationsAttr,
  numberCitationsInDom,
  parseCitedSources,
  sourceKey,
} from "./reportSources";
import { flagUnfilledPlaceholders } from "./reportUnfilled";
import { ReportCitationContext } from "./reportCitationContext";
import { Button } from "@/ui/primitives";
import { cn } from "@/utils/misc";

/** An inline source-citation chip (icon + label + type tag) that deep-links. */
function CitationChip({ source }: { source: CitedSource }) {
  const { onDocumentClick, onPodClaimClick, showInline } = useContext(
    ReportCitationContext,
  );
  if (showInline === false) return null;
  const meta = getKindMeta(source.kind);
  const Icon = meta.icon;
  const target = meta.buildTo({
    id: source.id,
    jobId: source.jobId,
    orgId: source.orgId,
  });
  const label =
    source.title?.trim() || `${meta.label} ${source.id.slice(0, 6)}`;
  // The chip floats over report body text, so every state (including hover)
  // needs a fully opaque background — a translucent tint lets the sentence
  // underneath show through and makes the source name unreadable.
  const body = (
    <span className="inline-flex max-w-[240px] items-center gap-1 rounded-md border border-border bg-popover px-1.5 py-0.5 align-baseline text-[0.8em] font-medium text-foreground no-underline shadow-md transition-colors hover:border-primary/40 hover:bg-accent">
      <Icon className="size-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-[0.7em] uppercase tracking-wider text-muted-foreground/60">
        {meta.label}
      </span>
    </span>
  );

  // Documents and POD claims open their detail view in-place instead of
  // navigating away — so a reader reviewing the report keeps their place. The
  // chip body carries that in-place action; kinds without an in-place view
  // leave the body as a plain label and rely on the separate open icon below.
  const inPlaceClick =
    source.kind === "document" && onDocumentClick
      ? () => onDocumentClick(source.id)
      : canonicalSearchKind(source.kind) === "pod_claim" && onPodClaimClick
        ? () => onPodClaimClick(source.id)
        : null;
  const chipBody = inPlaceClick ? (
    <button
      type="button"
      className="cursor-pointer no-underline"
      onClick={inPlaceClick}
    >
      {body}
    </button>
  ) : (
    body
  );

  // A distinct open icon that always takes the reader to the record's full
  // agent view (e.g. the Recoveries POD page, or a summary agent register) —
  // separate from the chip body so it's clear it navigates away.
  const openLink = target ? (
    <Link
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- route built from the kind table
      to={target.to as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
      params={target.params as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see above
      search={target.search as any}
      aria-label={`Open ${meta.label} view`}
      title={`Open ${meta.label} view`}
      className="inline-flex items-center rounded border border-border bg-popover p-0.5 align-baseline text-muted-foreground no-underline shadow-md transition-colors hover:border-primary/40 hover:text-primary"
    >
      <ArrowUpRight className="size-3" />
    </Link>
  ) : null;

  return (
    <span className="mx-0.5 inline-flex items-center gap-0.5 align-baseline">
      {chipBody}
      {openLink}
    </span>
  );
}

/**
 * Inline mark for a source citation (MOR-919). Round-trips as
 * `<span data-morgan-cite='<json>'>` and renders as a subtle underline over
 * the referenced text — click opens the source, a short hover reveals the
 * source chip(s) (see `CitationLayer`). Legacy title-chip spans (single
 * JSON-object attr, title as text) parse into the same mark and just render
 * their title underlined.
 */
const ReportCitation = Mark.create({
  name: "reportCitation",
  addAttributes() {
    return {
      sources: {
        default: [],
        parseHTML: (el: HTMLElement) =>
          citationsFromAttr(el.getAttribute("data-morgan-cite") ?? ""),
        renderHTML: (attrs: { sources: CitedSource[] }) =>
          attrs.sources && attrs.sources.length > 0
            ? { "data-morgan-cite": encodeCitationsAttr(attrs.sources) }
            : {},
      },
      citeText: {
        default: false,
        parseHTML: (el: HTMLElement) => el.hasAttribute("data-cite-text"),
        renderHTML: (attrs: { citeText: boolean }) =>
          attrs.citeText ? { "data-cite-text": "1" } : {},
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-morgan-cite]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", mergeAttributes(HTMLAttributes), 0];
  },
});

/**
 * Set on a citation span while its source chips are showing; styled in
 * `index.css` to draw a solid underline under the referenced passage.
 */
const CITE_ACTIVE_CLASS = "report-cite-active";

/**
 * Delegated interactivity for citation underlines inside a rendered report:
 * hovering a cited passage for a moment pops up its source chip(s) and marks
 * the passage those sources support; clicking opens the (single) source
 * directly, or the popover when there are several.
 */
export function CitationLayer({
  containerRef,
  markersOnly,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Only the superscript marker opens the chips, not the cited passage. Used
   * in the editor, where the passage has no underline to hover and the text is
   * being worked in.
   */
  markersOnly?: boolean;
}) {
  const { onDocumentClick, onPodClaimClick, showInline } = useContext(
    ReportCitationContext,
  );
  const navigate = useNavigate();
  const [popover, setPopover] = useState<{
    sources: CitedSource[];
    /** The citation the chips belong to — highlighted while they're shown. */
    span: HTMLElement;
    top: number;
    left: number;
    maxWidth: number;
  } | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const suppressed = showInline === false;

  useEffect(() => {
    const container = containerRef.current;
    if (!container || suppressed) return;

    const clearTimers = () => {
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
      hoverTimer.current = null;
      closeTimer.current = null;
    };

    const spanFor = (target: EventTarget | null): HTMLElement | null => {
      if (!(target instanceof Element)) return null;
      const span = target.closest(
        "span[data-morgan-cite]",
      ) as HTMLElement | null;
      if (!span) return null;
      if (markersOnly && !span.hasAttribute("data-cite-marker")) return null;
      return span;
    };

    const openFor = (span: HTMLElement) => {
      const sources = citationsFromAttr(
        span.getAttribute("data-morgan-cite") ?? "",
      );
      if (sources.length === 0) return;
      // Sit the chip immediately after the citation, on the same line — a
      // citation that wraps has several rects, and the marker is at the end
      // of the last one.
      const rects = span.getClientRects();
      const rect = rects[rects.length - 1] ?? span.getBoundingClientRect();
      setPopover({
        sources,
        span,
        top: rect.top,
        left: rect.right + 6,
        maxWidth: Math.max(180, window.innerWidth - rect.right - 24),
      });
    };

    const onOver = (e: MouseEvent) => {
      const span = spanFor(e.target);
      if (!span) return;
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
      hoverTimer.current = window.setTimeout(() => openFor(span), 350);
    };
    const onOut = (e: MouseEvent) => {
      const span = spanFor(e.target);
      if (!span) return;
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
      closeTimer.current = window.setTimeout(() => setPopover(null), 200);
    };
    const onClick = (e: MouseEvent) => {
      const span = spanFor(e.target);
      if (!span) return;
      const sources = citationsFromAttr(
        span.getAttribute("data-morgan-cite") ?? "",
      );
      if (sources.length === 0) return;
      e.preventDefault();
      e.stopPropagation();
      // While editing, a click shows the chips rather than following the source
      // — navigating straight out of the editor would drop unsaved edits.
      if (markersOnly || sources.length > 1) {
        openFor(span);
        return;
      }
      const source = sources[0];
      if (source.kind === "document" && onDocumentClick) {
        onDocumentClick(source.id);
        return;
      }
      if (canonicalSearchKind(source.kind) === "pod_claim" && onPodClaimClick) {
        onPodClaimClick(source.id);
        return;
      }
      const meta = getKindMeta(source.kind);
      const target = meta.buildTo({
        id: source.id,
        jobId: source.jobId,
        orgId: source.orgId,
      });
      if (target) {
        void navigate({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          to: target.to as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          params: target.params as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          search: target.search as any,
        });
      }
    };

    container.addEventListener("mouseover", onOver);
    container.addEventListener("mouseout", onOut);
    container.addEventListener("click", onClick);
    return () => {
      clearTimers();
      container.removeEventListener("mouseover", onOver);
      container.removeEventListener("mouseout", onOut);
      container.removeEventListener("click", onClick);
    };
  }, [
    containerRef,
    markersOnly,
    navigate,
    onDocumentClick,
    onPodClaimClick,
    suppressed,
  ]);

  // Mark the cited passage for as long as its chips are on screen — including
  // while the cursor is on a chip — so it's obvious which words the source
  // supports, not just that a source exists somewhere in the sentence.
  useEffect(() => {
    const span = popover?.span;
    if (!span || suppressed) return;
    span.classList.add(CITE_ACTIVE_CLASS);
    return () => span.classList.remove(CITE_ACTIVE_CLASS);
  }, [popover, suppressed]);

  if (!popover || suppressed) return null;
  // Rendered inline beside the citation rather than as a floating card, so it
  // reads as an annotation on the sentence it belongs to.
  return (
    <div
      className="fixed z-50 flex flex-wrap items-center gap-1"
      style={{
        top: popover.top,
        left: popover.left,
        maxWidth: popover.maxWidth,
      }}
      onMouseEnter={() => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
      }}
      onMouseLeave={() => setPopover(null)}
    >
      {popover.sources.map((s) => (
        <CitationChip key={`${s.kind}:${s.id}`} source={s} />
      ))}
    </div>
  );
}

/** Extensions shared by the report editor and the read-only renderer. */
const reportExtensions = [
  StarterKit,
  TextStyleKit,
  FontSizeNode,
  TableKit.configure({ table: { resizable: true } }),
  ChartNode,
  SpreadsheetNode,
  ReportCitation,
];

/**
 * The editor also draws the bibliography markers, which the renderer instead
 * stamps onto the rendered DOM (see `citationMarkersExtension`).
 */
const editorExtensions = [...reportExtensions, ReportCitationMarkers];
import {
  ArrowUpRight,
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Redo2,
  Table as TableIcon,
  Undo2,
} from "lucide-react";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

const FONT_SIZES = [
  { label: "Small", value: "9pt" },
  { label: "Normal", value: "11pt" },
  { label: "Medium", value: "13pt" },
  { label: "Large", value: "16pt" },
  { label: "X-Large", value: "20pt" },
  { label: "Heading", value: "26pt" },
] as const;

export function ReportEditor({
  initialHtml,
  onChange,
}: {
  initialHtml: string;
  /** Called with the latest HTML on every change (parent persists on Save). */
  onChange: (html: string) => void;
}) {
  const { showInline } = useContext(ReportCitationContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const editor = useEditor({
    extensions: editorExtensions,
    content: initialHtml,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        // `report-cite-markers` switches citations to their marker-only look:
        // no chip, and no underline over the cited passage — underlining text
        // the practitioner is working in makes it harder to read and edit.
        class:
          "prose prose-sm dark:prose-invert max-w-none focus:outline-none min-h-[400px] report-cite-markers",
        // Native browser spell check on report content (MOR-1034).
        spellcheck: "true",
      },
    },
  });

  if (!editor) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border">
        Loading editor…
      </div>
    );
  }

  const currentSize =
    (editor.getAttributes("textStyle")?.fontSize as string) || "";

  const tbBtn = (active: boolean) =>
    cn("h-7 w-7 p-0", active && "bg-accent text-accent-foreground");

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b bg-background/60 px-2 py-1.5">
        <select
          value={currentSize}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) {
              editor.chain().focus().unsetFontSize().unsetNodeFontSize().run();
            } else {
              editor.chain().focus().setFontSize(v).setNodeFontSize(v).run();
            }
          }}
          className="mr-1 h-7 rounded-md border bg-background px-1.5 text-xs"
          title="Font size"
        >
          <option value="">Size</option>
          {FONT_SIZES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("bold"))}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("italic"))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="size-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("heading", { level: 1 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          <Heading1 className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("heading", { level: 2 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          <Heading2 className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("heading", { level: 3 }))}
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          <Heading3 className="size-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("bulletList"))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={tbBtn(editor.isActive("orderedList"))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run()
          }
          title="Insert table"
        >
          <TableIcon className="size-3.5" />
        </Button>
        <div className="mx-1 h-4 w-px bg-border" />
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="size-3.5" />
        </Button>
      </div>

      {/* Editor surface */}
      <div
        ref={containerRef}
        className={cn(
          "flex-1 overflow-y-auto p-8",
          showInline === false && "hide-report-cites",
        )}
      >
        <EditorContent editor={editor} />
        {/* Hovering a marker reveals its source chips, as in the preview —
            without it the citations would be unreadable while editing. */}
        <CitationLayer containerRef={containerRef} markersOnly />
        <AiDisclaimer className="mt-6" />
      </div>
    </div>
  );
}

/**
 * Decorate the rendered report DOM: add `className` to every heading whose
 * text matches one of `titles` (case-insensitive) plus its section body (the
 * siblings up to the next same-or-higher heading). Returns the first matched
 * heading element.
 */
function decorateSections(
  root: HTMLElement,
  titles: string[],
  className: string,
): HTMLElement | null {
  root
    .querySelectorAll(`.${className}`)
    .forEach((el) => el.classList.remove(className));
  if (titles.length === 0) return null;
  const wanted = new Set(titles.map((t) => t.trim().toLowerCase()));
  const matchedHeadings = Array.from(
    root.querySelectorAll<HTMLElement>("h1, h2, h3"),
  ).filter((h) => wanted.has(h.textContent?.trim().toLowerCase() ?? ""));
  const first = matchedHeadings[0] ?? null;
  for (const h of matchedHeadings) {
    const level = Number(h.tagName[1]);
    h.classList.add(className);
    let el = h.nextElementSibling;
    while (el) {
      const m = /^H([1-6])$/.exec(el.tagName);
      if (m && Number(m[1]) <= level) break;
      el.classList.add(className);
      el = el.nextElementSibling;
    }
  }
  return first;
}

/**
 * Read-only renderer for the assembled report. Uses the same TipTap schema as
 * the editor so charts (and clickable source citations) render. Clicks bubble
 * to `onClick` so the parent can open a cited source document.
 */
export function ReportRenderer({
  html,
  onClick,
  footer,
  className,
  editingSections,
  taggedSection,
}: {
  html: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Optional content rendered below the report (e.g. the Sources list). */
  footer?: ReactNode;
  className?: string;
  /** MOR-917 — sections the assistant is redrafting right now (pulsing highlight). */
  editingSections?: string[];
  /** MOR-917 — the section the user @-tagged in the chat (highlight + scroll). */
  taggedSection?: string | null;
}) {
  const { showInline } = useContext(ReportCitationContext);
  const containerRef = useRef<HTMLDivElement>(null);
  // Strip any legacy baked-in "Sources" section (old reports); the list is now
  // rendered separately via `footer` so it can be toggled.
  const body = html.replace(/<h2>\s*Sources\s*<\/h2>[\s\S]*$/i, "");
  const editor = useEditor({
    editable: false,
    extensions: reportExtensions,
    content: body,
    editorProps: {
      attributes: {
        class: "prose prose-sm dark:prose-invert max-w-none focus:outline-none",
      },
    },
  });

  // Keep the rendered content in sync when the report changes (e.g. after Apply).
  useEffect(() => {
    if (editor && body !== editor.getHTML()) {
      editor.commands.setContent(body, { emitUpdate: false });
    }
  }, [body, editor]);

  // MOR-924 — number the inline citations against the bibliography. Applied
  // to the rendered DOM (not the stored HTML) so numbering stays a display
  // concern and never leaks into what we save or export.
  const citationNumbers = useMemo(
    () => buildCitationNumbers(parseCitedSources(body)),
    [body],
  );
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (containerRef.current) {
        numberCitationsInDom(containerRef.current, citationNumbers);
        flagUnfilledPlaceholders(containerRef.current);
      }
    }, 40);
    return () => window.clearTimeout(t);
  }, [citationNumbers, body, editor]);

  // MOR-917 — highlight sections being edited by the assistant, and the
  // section the user tagged. Runs on a short delay so TipTap has painted.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const root = containerRef.current;
      if (!root) return;
      decorateSections(root, editingSections ?? [], "report-section-editing");
      const tagged = decorateSections(
        root,
        taggedSection ? [taggedSection] : [],
        "report-section-tagged",
      );
      tagged?.scrollIntoView({ behavior: "smooth", block: "start" });
      // Re-flag after TipTap has painted (the citation effect may have run too early).
      flagUnfilledPlaceholders(root);
    }, 80);
    return () => window.clearTimeout(t);
  }, [editingSections, taggedSection, body, editor]);

  return (
    <div
      ref={containerRef}
      onClick={onClick}
      className={cn(className, showInline === false && "hide-report-cites")}
    >
      <EditorContent editor={editor} />
      <CitationLayer containerRef={containerRef} />
      {footer}
      <AiDisclaimer className="mt-6" />
    </div>
  );
}

/**
 * The consolidated, clickable "Sources" bibliography. Numbered to match the
 * superscript markers in the body, so a reader can go from a number in the
 * text to the source that backs it (MOR-924).
 */
export function ReportSourcesList({ sources }: { sources: CitedSource[] }) {
  const { onDocumentClick, onPodClaimClick } = useContext(
    ReportCitationContext,
  );
  const numbers = useMemo(() => buildCitationNumbers(sources), [sources]);
  if (sources.length === 0) return null;
  return (
    <div className="prose prose-sm mt-8 max-w-none border-t pt-6 dark:prose-invert">
      <h2>Sources</h2>
      <ol className="not-prose m-0 flex list-none flex-col gap-1.5 p-0">
        {sources.map((s) => (
          <li key={sourceKey(s)} className="flex items-baseline gap-2">
            <span className="w-5 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {numbers.get(sourceKey(s))}.
            </span>
            <SourceChips
              sources={[s]}
              onSourceClick={(clicked) => {
                if (clicked.kind === "document" && onDocumentClick) {
                  onDocumentClick(clicked.id);
                  return true;
                }
                if (
                  canonicalSearchKind(clicked.kind) === "pod_claim" &&
                  onPodClaimClick
                ) {
                  onPodClaimClick(clicked.id);
                  return true;
                }
                return false;
              }}
            />
          </li>
        ))}
      </ol>
    </div>
  );
}
